import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MediaRecorderConfig, MediaUploadService } from '../media-upload.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '@quorum/environments';

@Component({
  selector: 'sha-media-recorder-upload-dialog',
  templateUrl: './media-recorder-upload-dialog.component.html',
  styleUrls: ['./media-recorder-upload-dialog.component.scss'],
})
export class MediaRecorderUploadDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  mediaForm: FormGroup;
  maxCaptionLength = 75;
  displayMediaRecorder = false;
  mediaRecorder: MediaRecorder | null = null;
  recordedChunks: Blob[] = [];
  stream: MediaStream | null = null;
  currentCaptureMode: 'photo' | 'video' = 'photo';
  facingMode = 'environment';
  totalDuration = 0;
  totalPauseTime = 0;
  lastPauseTime = 0;
  startTime = 0;
  displayResumeIcon = false;
  isRecording = false;

  @ViewChild('previewElement', { static: false }) previewElement!: ElementRef<HTMLVideoElement>;
  captureButton: HTMLButtonElement;
  pauseButton: HTMLButtonElement;
  stopButton: HTMLButtonElement;
  switchCameraButton: HTMLButtonElement;

  // show media recorder debug panel in development
  showDebugPanel = environment.mediaDebugLogging;

  private chunkSizes: number[] = [];
  private recordingStartTime = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { caption: string; customerFacing: boolean; params: any },
    public dialogRef: MatDialogRef<MediaRecorderUploadDialogComponent>,
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private mediaUploadService: MediaUploadService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.mediaForm = this.fb.group({
      mediaToUpload: this.fb.array([]),
    });
  }

  ngOnDestroy(): void {
    this.stopStream();
  }

  ngAfterViewInit(): void {
    this.captureButton = document.getElementById('captureButton') as HTMLButtonElement;
    this.pauseButton = document.getElementById('pauseButton') as HTMLButtonElement;
    this.stopButton = document.getElementById('stopButton') as HTMLButtonElement;
    this.switchCameraButton = document.getElementById('switchCameraButton') as HTMLButtonElement;
  }

  getMediaArray(): FormArray {
    return this.mediaForm.get('mediaToUpload') as FormArray;
  }

  hasMediaItems(): boolean {
    const mediaToUpload = this.mediaForm.get('mediaToUpload') as FormArray;
    return mediaToUpload && mediaToUpload.length > 0;
  }

  toggleCustomerFacing(index: number): void {
    const mediaArray = this.getMediaArray();
    const customerFacingControl = mediaArray.at(index).get('customerFacing');
    if (customerFacingControl) {
      customerFacingControl.setValue(!customerFacingControl.value);
    }
  }

  removeMedia(index: number): void {
    const mediaArray = this.getMediaArray();
    if (mediaArray && mediaArray.length > index) {
      mediaArray.removeAt(index);
    }
  }

  onFileSelected(e: any) {
    const files = e.target.files || e.dataTransfer.files;
    if (!files.length) return;

    const file = files[0];

    if (!(file.type.startsWith('image/') || file.type.startsWith('video/'))) {
      alert('Invalid file type. Please select an image or video file.');
      e.target.value = '';
      return;
    }

    let fileSize = '';
    if (file.size < 1024) {
      fileSize = `${file.size} B`;
    } else if (file.size < 1048576) {
      fileSize = `${(file.size / 1024).toFixed(2)} KB`;
    } else if (file.size < 1073741824) {
      fileSize = `${(file.size / 1048576).toFixed(2)} MB`;
    } else {
      fileSize = `${(file.size / 1073741824).toFixed(2)} GB`;
    }

    let imageSrc: string | null = null;
    let videoSrc: SafeUrl | null = null;

    if (file.type.split('/')[0] === 'image') {
      const reader = new FileReader();
      reader.onload = (e) => {
        imageSrc = e.target.result as string;
        const mediaArray = this.getMediaArray();
        mediaArray.push(
          this.fb.group({
            file: [file],
            fileType: [`.${file.type.split('/')[1]}`],
            fileSize: [fileSize],
            imageSrc: [imageSrc],
            videoSrc: [videoSrc],
            caption: [this.data.caption.substring(0, this.maxCaptionLength)],
            customerFacing: [this.data.customerFacing],
          })
        );
      };
      reader.readAsDataURL(file);
    } else if (file.type.split('/')[0] === 'video') {
      videoSrc = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
      const mediaArray = this.getMediaArray();
      mediaArray.push(
        this.fb.group({
          file: [file],
          fileType: [`.${file.type.split('/')[1]}`],
          fileSize: [fileSize],
          imageSrc: [imageSrc],
          videoSrc: [videoSrc],
          caption: [this.data.caption.substring(0, this.maxCaptionLength)],
          customerFacing: [this.data.customerFacing],
        })
      );
    }
  }

  initializeMediaUpload() {
    this.createImage(this.mediaForm.get('mediaToUpload').value);
  }

  createImage(
    mediaToUpload: {
      file: File;
      fileType: string;
      fileSize: string;
      imageSrc: string | null;
      videoSrc: SafeUrl | null;
      caption: string;
      customerFacing: boolean;
      duration: number;
    }[]
  ) {
    mediaToUpload.forEach((media) => {
      if (!media.file || media.file.size === 0) {
        this.snackBar.open('Invalid file provided', null, { duration: 3000 });
        return;
      }

      const fileName: string =
        this.mediaUploadService.generateId(12) + media.file.name.substring(media.file.name.lastIndexOf('.'));

      if (media.file.type.startsWith('video/')) {
        this.processVideo(media.caption, media.customerFacing, media.file, fileName, media.duration);
      } else if (media.file.type.startsWith('image/')) {
        this.mediaUploadService.uploadMediaRecorderFile(
          media.caption,
          media.customerFacing,
          fileName,
          media.file.type,
          null,
          media.file.size,
          media.file,
          this.data.params
        );
      } else {
        alert('Wrong file type - Video/Image only.');
      }
    });
  }

  processVideo(
    caption: string,
    customerFacing: boolean,
    file: File,
    fileName: string,
    videoDuration: number
  ) {
    console.log('Processing video:', fileName);

    // File validation
    if (!file || file.size === 0) {
      this.snackBar.open(`Invalid file provided`, null, { duration: 3000 });
      return;
    }

    try {
      const url = (URL || webkitURL).createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';

      // Cleanup to prevent memory leaks
      const cleanup = () => {
        (URL || webkitURL).revokeObjectURL(url);
        video.remove();
      };

      const upload = (duration: number | null) => {
        this.mediaUploadService.uploadMediaRecorderFile(
          caption,
          customerFacing,
          fileName,
          file.type,
          duration,
          file.size,
          file,
          this.data.params
        );
      };

      // If metadata can't be read, still upload rather than silently dropping the file.
      video.addEventListener('error', (e) => {
        console.error('Video loading error:', e);
        cleanup();
        upload(videoDuration ? videoDuration : null);
      });

      video.addEventListener('loadedmetadata', () => {
        const duration: number = videoDuration ? videoDuration : video.duration;
        cleanup();
        upload(duration);
      });

      video.src = url;
    } catch (error) {
      this.snackBar.open(`Error processing video, please try again later`, null, { duration: 3000 });
      throw error;
    }
  }

  captureMedia(mode: 'photo' | 'video') {
    this.displayMediaRecorder = true;
    this.currentCaptureMode = mode;
    this.displayResumeIcon = false;
    this.facingMode = 'environment';
    this.initializeCapture();
  }

  async initializeCapture() {
    try {
      if (this.stream) {
        this.stopStream();
      }
      const constraints = {
        video: {
          facingMode: this.facingMode,

          width: { ideal: 1920, max: 1920 },
          height: { ideal: 1080, max: 1080 },
          frameRate: { ideal: 30 },

          // iPad-specific optimizations
          aspectRatio: { ideal: 16 / 9 },
          resizeMode: 'crop-and-scale',
        },
        audio:
          this.currentCaptureMode === 'video'
            ? {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
                sampleRate: 44100,
                channelCount: 1,
                suppressLocalAudioPlayback: true,
              }
            : false,
      };
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Ensure the video element is muted to prevent feedback
      if (this.previewElement) {
        this.previewElement.nativeElement.srcObject = this.stream;
        this.previewElement.nativeElement.volume = 0; // Ensures no audio playback
      }
      this.previewElement.nativeElement.srcObject = this.stream;
      this.previewElement.nativeElement.style.display = 'block';

      if (this.facingMode === 'user') {
        this.previewElement.nativeElement.classList.add('user-facing');
      } else {
        this.previewElement.nativeElement.classList.remove('user-facing');
      }

      this.captureButton.onclick =
        this.currentCaptureMode === 'photo' ? this.takePhoto.bind(this) : this.startRecording.bind(this);
      this.pauseButton.onclick = this.pauseRecording.bind(this);
      this.stopButton.onclick = this.stopCapture.bind(this);
      this.switchCameraButton.onclick = this.switchCamera.bind(this);

      await new Promise((resolve) => setTimeout(resolve, 1000));
    } catch (err) {
      console.error('Error accessing media devices:', err);
      alert('Error accessing camera/microphone: ' + err.message);
    }
  }

  async switchCamera() {
    this.facingMode = this.facingMode === 'environment' ? 'user' : 'environment';
    await this.stopStream();
    this.initializeCapture();
  }

  takePhoto() {
    const canvas = document.createElement('canvas');
    canvas.width = this.previewElement.nativeElement.videoWidth;
    canvas.height = this.previewElement.nativeElement.videoHeight;
    const canvasContext = canvas.getContext('2d');

    if (this.facingMode === 'user') {
      canvasContext.scale(-1, 1);
      canvasContext.drawImage(this.previewElement.nativeElement, 0, 0, -canvas.width, canvas.height);
    } else {
      canvasContext.drawImage(this.previewElement.nativeElement, 0, 0, canvas.width, canvas.height);
    }

    const imageSrc = canvas.toDataURL('image/jpeg', 1.0);

    const byteString = atob(imageSrc.split(',')[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([uint8Array], { type: 'image/jpeg' });

    const file = new File([blob], `photo-${crypto.randomUUID().slice(0, 8)}.jpg`, { type: 'image/jpeg' });

    const fileSize =
      file.size < 1024
        ? `${file.size} B`
        : file.size < 1048576
        ? `${(file.size / 1024).toFixed(2)} KB`
        : file.size < 1073741824
        ? `${(file.size / 1048576).toFixed(2)} MB`
        : `${(file.size / 1073741824).toFixed(2)} GB`;

    const mediaArray = this.getMediaArray();
    mediaArray.push(
      this.fb.group({
        file: [file],
        fileType: ['.jpg'],
        fileSize: [fileSize],
        imageSrc: [imageSrc],
        videoSrc: [null],
        caption: [this.data.caption.substring(0, this.maxCaptionLength)],
        customerFacing: [this.data.customerFacing],
      })
    );

    this.closeCaptureModal();
  }

  closeCaptureModal(): void {
    this.displayMediaRecorder = false;
    this.isRecording = false;
    this.stopStream();
  }

  async startRecording() {
    this.recordedChunks = [];
    this.chunkSizes = [];
    this.mediaUploadService.debugLogs = [];
    this.totalDuration = 0;
    this.totalPauseTime = 0;
    this.lastPauseTime = 0;
    this.startTime = performance.now() / 1000;
    this.recordingStartTime = Date.now();

    let options: MediaRecorderOptions;
    let config: MediaRecorderConfig;

    try {
      // Get quality level based on use case
      //const qualityPresets = this.mediaUploadService.getQualityPresets();
      //const qualityLevel = qualityPresets[useCase] || 'balanced';
      //this.mediaUploadService.debugLog(`Use case: ${useCase}, Quality: ${qualityLevel}`);

      const qualityLevel = 'balanced';

      this.mediaUploadService.debugLog(`Use case: balanced, Quality: ${qualityLevel}`);

      // Generate optimal configuration
      config = await this.mediaUploadService.generateOptimalConfig(this.stream, qualityLevel);

      this.mediaUploadService.debugLog('=== FINAL CONFIGURATION ===');
      this.mediaUploadService.debugLog(`MIME: ${config.mimeType}`);
      this.mediaUploadService.debugLog(`Video: ${(config.videoBitsPerSecond / 1000).toFixed(0)}kbps`);
      this.mediaUploadService.debugLog(`Audio: ${(config.audioBitsPerSecond / 1000).toFixed(0)}kbps`);
      this.mediaUploadService.debugLog(`Timeslice: ${config.timeslice}ms`);

      // Create MediaRecorder with optimal settings
      options = {
        mimeType: config.mimeType,
        videoBitsPerSecond: config.videoBitsPerSecond,
        audioBitsPerSecond: config.audioBitsPerSecond,
      };

      this.mediaRecorder = new MediaRecorder(this.stream, options);
    } catch (e) {
      this.mediaUploadService.criticalLog('MediaRecorder creation failed', e);
      return;
    }

    this.mediaRecorder.ondataavailable = (event) => {
      const timestamp = Date.now() - this.recordingStartTime;

      if (event.data && event.data.size > 0) {
        const chunkNum = this.recordedChunks.length + 1;
        this.mediaUploadService.debugLog(`Chunk #${chunkNum}: ${event.data.size} bytes`);

        this.recordedChunks.push(event.data);
        this.chunkSizes.push(event.data.size);

        // Every 5 chunks, show summary
        if (chunkNum % 5 === 0) {
          const totalSize = this.chunkSizes.reduce((sum, size) => sum + size, 0);
          this.mediaUploadService.debugLog(`Summary: ${chunkNum} chunks, ${this.formatFileSize(totalSize)}`);
        }
      } else {
        this.mediaUploadService.debugLog(`Empty chunk at ${timestamp}ms`);
      }
    };

    this.mediaRecorder.onstart = () => {
      this.mediaUploadService.debugLog('=== Recording Started ===');
      this.isRecording = true;
    };

    this.mediaRecorder.onstop = () => {
      this.mediaUploadService.debugLog('=== Recording Stopped ===');
      this.mediaUploadService.debugLog(`Total chunks: ${this.recordedChunks.length}`);

      if (this.recordedChunks.length === 0) {
        this.mediaUploadService.criticalLog('CRITICAL: No chunks received!');
        return;
      }

      const expectedSize = this.chunkSizes.reduce((sum, size) => sum + size, 0);
      this.mediaUploadService.debugLog(`Expected size: ${expectedSize} bytes`);

      try {
        const blobMimeType = this.mediaRecorder.mimeType || 'video/mp4';
        this.mediaUploadService.debugLog(`Creating blob with: ${blobMimeType}`);

        const blob = new Blob(this.recordedChunks, { type: blobMimeType });

        this.mediaUploadService.debugLog(`Blob size: ${blob.size} (expected: ${expectedSize})`);

        if (blob.size === 0) {
          this.mediaUploadService.criticalLog('CRITICAL: Blob size is 0!', {
            chunks: this.recordedChunks.length,
            sizes: this.chunkSizes,
          });
          return;
        }

        let extension = '.mp4';
        let fileType = 'video/mp4';

        if (blobMimeType.includes('webm')) {
          extension = '.webm';
          fileType = 'video/webm';
        }

        const file = new File([blob], `video-${crypto.randomUUID().slice(0, 8)}${extension}`, {
          type: fileType,
        });

        this.mediaUploadService.debugLog(`File created: ${file.size} bytes`);

        const fileSize = this.formatFileSize(file.size);
        const videoSrc = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));

        const mediaArray = this.getMediaArray();
        mediaArray.push(
          this.fb.group({
            file: [file],
            fileType: [extension],
            fileSize: [fileSize],
            imageSrc: [null],
            videoSrc: [videoSrc],
            caption: [this.data.caption.substring(0, this.maxCaptionLength)],
            customerFacing: [this.data.customerFacing],
            duration: [this.totalDuration || 0],
          })
        );

        this.mediaUploadService.debugLog('SUCCESS: File added to form');
        if (this.showDebugPanel) {
          this.debugPostRecording(file);
        }
        this.recordedChunks = [];
        this.chunkSizes = [];
        this.closeCaptureModal();
      } catch (error) {
        this.mediaUploadService.criticalLog('Blob creation error', error);
      }
    };

    this.mediaRecorder.onerror = (event) => {
      this.mediaUploadService.criticalLog('MediaRecorder error', event);
    };

    this.mediaUploadService.debugLog(
      `Starting with timeslice: ${config.timeslice}ms, bitrate: ${config.videoBitsPerSecond}`
    );
    this.mediaRecorder.start(config.timeslice);
  }

  exportDebugLogs() {
    const logData = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      totalLogEntries: this.mediaUploadService.logFile.length,
      displayedEntries: this.mediaUploadService.debugLogs.length,
      logs: this.mediaUploadService.logFile, // Export ALL logs, not just displayed ones
      recordingStats: {
        totalChunks: this.recordedChunks?.length || 0,
        chunkSizes: this.chunkSizes || [],
        totalDuration: this.totalDuration || 0,
      },
    };

    const blob = new Blob([JSON.stringify(logData, null, 2)], {
      type: 'application/json',
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mediarecorder-full-debug-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  clearDebugLogs() {
    this.mediaUploadService.debugLogs = [];
  }

  private formatFileSize(size: number): string {
    if (size < 1024) return `${size} B`;
    if (size < 1048576) return `${(size / 1024).toFixed(2)} KB`;
    if (size < 1073741824) return `${(size / 1048576).toFixed(2)} MB`;
    return `${(size / 1073741824).toFixed(2)} GB`;
  }

  pauseRecording() {
    if (this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.pause();
      this.lastPauseTime = performance.now() / 1000;
    } else if (this.mediaRecorder.state === 'paused') {
      this.mediaRecorder.resume();
      this.totalPauseTime += performance.now() / 1000 - this.lastPauseTime;
      this.lastPauseTime = 0;
    }
  }

  stopCapture() {
    if (this.currentCaptureMode === 'video' && this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
  }

  stopStream() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }
  }

  async switchMediaCaptureMode(captureMode: 'photo' | 'video') {
    if (this.currentCaptureMode !== captureMode) {
      this.currentCaptureMode = captureMode;
      await this.stopStream();
      this.initializeCapture();
    }
  }

  cancel() {
    this.dialogRef.close();
  }

  private debugPostRecording(file: File) {
    this.mediaUploadService.debugLog(`=== POST-RECORDING DEBUG ===`);
    this.mediaUploadService.debugLog(`File object: name=${file.name}, size=${file.size}, type=${file.type}`);

    // Check if file is readable
    const reader = new FileReader();
    reader.onload = () => {
      this.mediaUploadService.debugLog(`File readable: ${reader.result ? 'YES' : 'NO'}`);
    };
    reader.onerror = () => {
      this.mediaUploadService.debugLog(`File read ERROR: ${reader.error}`);
    };
    reader.readAsArrayBuffer(file.slice(0, 1024)); // Read first 1KB as test

    // Check form array
    const mediaArray = this.getMediaArray();
    this.mediaUploadService.debugLog(`Media array length: ${mediaArray.length}`);

    if (mediaArray.length > 0) {
      const lastItem = mediaArray.at(mediaArray.length - 1);
      const formFile = lastItem?.get('file')?.value;
      const formSize = lastItem?.get('fileSize')?.value;

      this.mediaUploadService.debugLog(`Form file: size=${formFile?.size}, formSize=${formSize}`);

      // Check if file object is the same reference
      this.mediaUploadService.debugLog(`Same file reference: ${formFile === file}`);
    }
  }

  async testCameraCapabilities() {
    this.mediaUploadService.debugLogs = [];
    this.mediaUploadService.logFile = [];

    this.mediaUploadService.debugLog('=== CAMERA CAPABILITY TEST ===');

    if (!this.stream) {
      this.mediaUploadService.debugLog('No stream available');
      return;
    }

    const videoTrack = this.stream.getVideoTracks()[0];
    if (!videoTrack) {
      this.mediaUploadService.debugLog('No video track found');
      return;
    }

    try {
      const settings = videoTrack.getSettings();
      const capabilities = videoTrack.getCapabilities();
      const constraints = videoTrack.getConstraints();

      this.mediaUploadService.debugLog('=== CURRENT SETTINGS ===');
      this.mediaUploadService.debugLog(`Resolution: ${settings.width}x${settings.height}`);
      this.mediaUploadService.debugLog(`Frame rate: ${settings.frameRate}fps`);
      this.mediaUploadService.debugLog(`Aspect ratio: ${settings.aspectRatio}`);
      this.mediaUploadService.debugLog(`Facing mode: ${settings.facingMode}`);
      this.mediaUploadService.debugLog(`Device ID: ${settings.deviceId}`);

      this.mediaUploadService.debugLog('=== CAPABILITIES ===');
      if (capabilities.width) {
        this.mediaUploadService.debugLog(`Width: ${capabilities.width.min}-${capabilities.width.max}`);
      }
      if (capabilities.height) {
        this.mediaUploadService.debugLog(`Height: ${capabilities.height.min}-${capabilities.height.max}`);
      }
      if (capabilities.frameRate) {
        this.mediaUploadService.debugLog(`FPS: ${capabilities.frameRate.min}-${capabilities.frameRate.max}`);
      }

      this.mediaUploadService.debugLog('=== CONSTRAINTS ===');
      this.mediaUploadService.debugLog(`Constraints: ${JSON.stringify(constraints)}`);

      // Calculate recommended bitrate
      const pixels = settings.width * settings.height;
      const fps = settings.frameRate || 30;

      this.mediaUploadService.debugLog('=== BITRATE CALCULATIONS ===');
      this.mediaUploadService.debugLog(`Total pixels: ${pixels.toLocaleString()}`);

      // Different quality recommendations
      const conservative = Math.round((pixels * fps * 0.05) / 1000); // 0.05 bits/pixel/frame
      const balanced = Math.round((pixels * fps * 0.08) / 1000); // 0.08 bits/pixel/frame
      const aggressive = Math.round((pixels * fps * 0.12) / 1000); // 0.12 bits/pixel/frame

      this.mediaUploadService.debugLog(
        `Conservative bitrate: ${conservative}kbps (${(conservative / 1000).toFixed(1)}Mbps)`
      );
      this.mediaUploadService.debugLog(`Balanced bitrate: ${balanced}kbps (${(balanced / 1000).toFixed(1)}Mbps)`);
      this.mediaUploadService.debugLog(`Aggressive bitrate: ${aggressive}kbps (${(aggressive / 1000).toFixed(1)}Mbps)`);

      // Current settings analysis
      const currentBitrate = 4000; // Your current setting in kbps
      this.mediaUploadService.debugLog(`Current setting: ${currentBitrate}kbps`);

      if (currentBitrate < conservative) {
        this.mediaUploadService.debugLog('⚠️ Current bitrate may be too low for good quality');
      } else if (currentBitrate > aggressive) {
        this.mediaUploadService.debugLog('⚠️ Current bitrate may be too high and cause issues');
      } else {
        this.mediaUploadService.debugLog('✅ Current bitrate looks reasonable');
      }
    } catch (error) {
      this.mediaUploadService.debugLog(`Error getting camera info: ${error}`);
    }
  }

  toggleLogSize() {
    this.mediaUploadService.maxDebugLogs = this.mediaUploadService.maxDebugLogs > 50 ? 50 : 200;
  }
}
